import { useState } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { z } from "zod";
import {
  AdminOrganizationControllerUpdateRequestBody,
  AdminOrganizationControllerUpdateResponse,
  useAdminOrganizationControllerUpdate,
} from "service";
import { AdminOrganizationDetailResDto } from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { queryClient, skipGlobalErrorToast } from "@/query-client";
import useAddressValidation from "@/hooks/use-address-validation";
import { optionalEmailSchema } from "@/shared/utils/email-validation";
import { EditOrganizationFormValues } from "../types/organization-form";
import {
  buildUpdateOrganizationFormData,
  getErrorDetail,
  getHomeImageErrorMessage,
  getPromotionalBannerInitialValues,
  promotionalBannerValidationSchema,
} from "../utils/promotional-banner";

const useEditOrganizationForm = (
  organization: AdminOrganizationDetailResDto,
  onFormSuccess: (data: AdminOrganizationControllerUpdateResponse) => void,
) => {
  const {
    validateAddress,
    getPlacePredictions,
    placePredictions,
    getAddressComponents,
  } = useAddressValidation({ bounds: null });

  // Same "must match a live Places prediction" rule as Add (ESR-1632), with
  // one carve-out: the organization's already-saved address was validated
  // when it was set and has no live prediction to match on load, so it
  // can't be run through validateAddress again here - only a *new* edit
  // needs to resolve to a fresh selection.
  const validationSchema = z.intersection(
    z.object({
      name: z.string().min(1, "Organization name is required"),
      address: z
        .string()
        .min(1, "Address is required")
        .refine(
          (address) =>
            address === organization.address || validateAddress(address),
          { message: "Invalid address." },
        ),
      state: z.string().min(1, "State is required"),
      city: z.string().optional().nullable(),
      zipCode: z.string().optional().nullable(),
      replyToEmail: optionalEmailSchema.optional().nullable(),
    }),
    promotionalBannerValidationSchema,
  );

  const [homeImageError, setHomeImageError] = useState<string | null>(null);

  const { mutate, isLoading } = useAdminOrganizationControllerUpdate({
    onSuccess: (data) => {
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/organizations",
          operationId: "adminOrganizationControllerFindMany",
          variables: {},
        }),
      );
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/organizations/{id}",
          operationId: "adminOrganizationControllerFindById",
          variables: { pathParams: { id: organization.id } },
        }),
      );
      onFormSuccess(data);
    },
    meta: skipGlobalErrorToast,
    onError: (error) => {
      const imageError = getHomeImageErrorMessage(error);
      if (imageError) {
        // Show it in the drop zone and drop the rejected file. Saving stays
        // blocked until another file is picked or the image is dropped.
        setHomeImageError(imageError);
        organizationForm.setFieldValue("homeImageFile", null);
      }
      // One toast with the backend's reason (the global handler is skipped).
      toast.error(
        getErrorDetail(error) ??
          "Couldn't update the organization. Please try again.",
      );
    },
  });

  const organizationForm = useFormik<EditOrganizationFormValues>({
    initialValues: {
      name: organization.name,
      address: organization.address ?? "",
      state: organization.state,
      city: organization.city ?? "",
      zipCode: organization.zipCode ?? "",
      replyToEmail: organization.replyToEmail ?? "",
      ...getPromotionalBannerInitialValues(organization.homeImage),
    },
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit: (values) => {
      setHomeImageError(null);
      // The endpoint is multipart (ESR-1655), so a blank replyToEmail/city/
      // zipCode is sent as "" - which PATCH treats as "clear".
      mutate({
        pathParams: { id: organization.id },
        // The generated body type can't express FormData.
        body: buildUpdateOrganizationFormData(
          values,
        ) as unknown as AdminOrganizationControllerUpdateRequestBody,
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
  });

  return {
    organizationForm,
    homeImageError,
    clearHomeImageError: () => setHomeImageError(null),
    isLoading,
    getPlacePredictions,
    placePredictions,
    getAddressComponents,
  };
};

export default useEditOrganizationForm;
